import { useMutation } from '@apollo/client';
import { differenceInDays } from 'date-fns';
import React from 'react';
import { useIntl } from 'react-intl';

import { useServiceWorker } from '@xing-com/crate-core-service-worker-loader';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import {
  InViewTracking,
  useNotificationsTracking,
} from '@xing-com/crate-notifications-tracking';
import { Dialog } from '@xing-com/dialog';
import { useVisitorId } from '@xing-com/hub';

import { CreateWebPushSubscriptionDocument } from './graphql/create-web-push-subscription.gql-types';
import { UpdateWebPushSubscriptionDocument } from './graphql/update-web-push-subscription.gql-types';
import { publicKeyToUint8Array } from './helper';
export interface ContainerProps {
  jobId: string;
}

export const Container: React.FC<ContainerProps> = ({ jobId }) => {
  const intl = useIntl();
  const { isLoggedIn } = useLoginState();
  const isMediaPrint = useMatchMedia('print');
  const [showDialog, setShowDialog] = React.useState(false);
  const { serviceWorkerRegistration } = useServiceWorker();
  const [notificationPermission, setNotificationPermission] =
    React.useState<NotificationPermission>('default');

  const visitorId = useVisitorId();
  const [createWebPushSubscriptionMutation] = useMutation(
    CreateWebPushSubscriptionDocument
  );
  const [updateWebPushSubscriptionMutation] = useMutation(
    UpdateWebPushSubscriptionDocument
  );
  const { trackNotifications, trackNotificationsNWT } =
    useNotificationsTracking();

  //Check if a week has passed since the last dialog was shown
  const isTimePassedAfterLastDialog = (): boolean => {
    const currentTimestamp = Date.now();
    const lastTimeDialogShown = localStorage.getItem(
      'lastTimeNotificationsDialogShown'
    );

    const daysSinceLastDialogShown = lastTimeDialogShown
      ? differenceInDays(currentTimestamp, parseInt(lastTimeDialogShown))
      : 10;

    return daysSinceLastDialogShown >= 7 ? true : false;
  };

  const close = (): void => {
    setShowDialog(false);
    localStorage.setItem(
      'lastTimeNotificationsDialogShown',
      Date.now().toString()
    );
  };

  const confirm = (): void => {
    setShowDialog(false);
    Notification.requestPermission().then((permission) => {
      setNotificationPermission(permission);
      handleCreateSubscription();

      if (permission === 'granted') {
        trackNotifications({
          PropTrackAction: 'push_notification_prompt_accept',
          PropActionOrigin: 'push_similar_job',
        });

        trackNotificationsNWT({
          event: 'activated',
          element: 'push_notification_prompt',
          elementDetail: 'push_similar_job',
          itemUrn: 'urn:x-xing:jobs:posting:' + jobId.split('.')[0],
        });
      }
    });
    localStorage.removeItem('lastTimeNotificationsDialogShown');
  };

  React.useEffect(() => {
    setTimeout(() => {
      if (
        Notification.permission === 'default' &&
        isTimePassedAfterLastDialog()
      ) {
        setShowDialog(true);
      }
    }, 4000);
  }, []);

  //Check users permission every 5 minutes and update in DB
  React.useEffect(() => {
    const interval = setInterval(() => {
      if (
        Notification.permission !== notificationPermission &&
        localStorage.getItem('endpointUrl') !== null &&
        localStorage.getItem('keyAuthDetails') !== null &&
        localStorage.getItem('keyP256dh') !== null
      ) {
        handleUpdateSubscription(
          localStorage.getItem('endpointUrl') ?? '',
          localStorage.getItem('keyAuthDetails') ?? '',
          localStorage.getItem('keyP256dh') ?? ''
        );
      }

      setNotificationPermission(Notification.permission);
      if (Notification.permission === 'denied') {
        setShowDialog(false);
      }
    }, 300000);

    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for messages from the service worker
  React.useEffect(() => {
    const handleSeenNotificationTracking = (event: {
      data: { type: string };
    }): void => {
      if (event?.data?.type === 'notification-seen') {
        trackNotificationsNWT({
          event: 'entered_viewport',
          element: 'push_notification',
          elementDetail: 'push_similar_job',
          itemUrn: 'urn:x-xing:jobs:posting:' + jobId.split('.')[0],
        });
      }
    };

    if (localStorage.getItem('trackingEventListener') !== null) {
      navigator.serviceWorker?.addEventListener(
        'message',
        handleSeenNotificationTracking
      );
    } else {
      localStorage.setItem('trackingEventListener', 'true');
    }

    return () => {
      navigator.serviceWorker?.removeEventListener(
        'message',
        handleSeenNotificationTracking
      );
      localStorage.removeItem('trackingEventListener');
    };
  }, []);

  const handleUpdateSubscription = async (
    endpointUrl: string,
    keyAuthDetails: string,
    keyP256dh: string
  ): Promise<void> => {
    await updateWebPushSubscriptionMutation({
      variables: {
        input: {
          notificationEnv: 'web',
          visitorId: visitorId,
          subscriptionStatus: Notification.permission,
          url: endpointUrl,
          authDetails: keyAuthDetails,
          ellipticCurve: keyP256dh,
          locale: intl.locale,
        },
      },
    });
  };

  const handleSubscriptionBrowser = (publicKeyUint8: Uint8Array): void => {
    navigator.serviceWorker?.controller?.postMessage({
      type: '@xing-com/crate-notifications-job-details-push-notification',
      payload: new Date().toISOString(),
    });

    serviceWorkerRegistration.pushManager
      .subscribe({
        userVisibleOnly: true,
        applicationServerKey: publicKeyUint8,
      })
      .then((subscription) => {
        localStorage.setItem('endpointUrl', subscription?.endpoint || '');
        localStorage.setItem(
          'keyAuthDetails',
          subscription?.toJSON()?.keys?.auth || ''
        );
        localStorage.setItem(
          'keyP256dh',
          subscription?.toJSON()?.keys?.p256dh || ''
        );
        handleUpdateSubscription(
          subscription?.endpoint,
          subscription?.toJSON()?.keys?.auth || '',
          subscription?.toJSON()?.keys?.p256dh || ''
        );
      });
  };

  const handleCreateSubscription = async (): Promise<void> => {
    await createWebPushSubscriptionMutation({
      variables: {
        input: {
          notificationEnv: 'web',
          visitorId,
          jobId,
          locale: intl.locale,
        },
      },
    }).then(
      (response) => {
        const publicKeyResponse =
          response?.data?.createWebPushSubscription?.__typename ===
          'WebPushSubscription'
            ? response?.data?.createWebPushSubscription?.publicKey
            : '';

        localStorage.setItem('publicKey', publicKeyResponse || '');
        handleSubscriptionBrowser(
          publicKeyToUint8Array(publicKeyResponse || '')
        );
      },
      (_error) => {
        if (localStorage.getItem('publicKey') !== null) {
          handleSubscriptionBrowser(
            publicKeyToUint8Array(localStorage.getItem('publicKey') || '')
          );
        }
      }
    );
  };

  if (isLoggedIn) {
    return null;
  }

  const dialogBody = intl.formatMessage(
    { id: 'WEB_PUSH_NOTIFICATION_NEW_JOB_AD_BODY_1' },
    {
      space: () => (
        <>
          <br />
          <br />
        </>
      ),
    }
  );

  return (
    showDialog &&
    !isMediaPrint && (
      <InViewTracking
        nwtTrackingOptions={{
          element: 'push_notification_prompt',
          elementDetail: 'push_similar_job',
          itemUrn: 'urn:x-xing:jobs:posting:' + jobId.split('.')[0],
        }}
      >
        <Dialog
          headline={intl.formatMessage({
            id: 'WEB_PUSH_NOTIFICATION_NEW_JOB_AD_HEADLINE',
          })}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          text={dialogBody}
          confirmLabel={intl.formatMessage({
            id: 'WEB_PUSH_NOTIFICATION_NEW_JOB_AD_MAIN_CTA',
          })}
          cancelLabel={intl.formatMessage({
            id: 'WEB_PUSH_NOTIFICATION_NEW_JOB_AD_SECONDARY_CTA',
          })}
          onCancel={close}
          onConfirm={confirm}
          show={showDialog}
        />
      </InViewTracking>
    )
  );
};
